import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(() => {
        try {
            const raw = localStorage.getItem("su_profile");
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem("su_token");
        if (!token) {
            setUser(null);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            localStorage.removeItem("su_token");
            localStorage.removeItem("su_profile");
            setUser(null);
            setProfile(null);
        }
        setLoading(false);
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("su_token", data.token);
        localStorage.removeItem("su_profile");  // force profile picker
        setProfile(null);
        setUser(data.user);
        return data.user;
    };

    const register = async (email, password, name) => {
        const { data } = await api.post("/auth/register", { email, password, name });
        localStorage.setItem("su_token", data.token);
        localStorage.removeItem("su_profile");
        setProfile(null);
        setUser(data.user);
        return data.user;
    };

    const logout = () => {
        localStorage.removeItem("su_token");
        localStorage.removeItem("su_profile");
        setUser(null);
        setProfile(null);
    };

    const selectProfile = async (profile_id, pin = null) => {
        const { data } = await api.post("/profiles/select", { profile_id, pin });
        localStorage.setItem("su_token", data.token);
        localStorage.setItem("su_profile", JSON.stringify(data.profile));
        setProfile(data.profile);
        return data.profile;
    };

    const switchProfile = () => {
        localStorage.removeItem("su_profile");
        setProfile(null);
    };

    return (
        <AuthContext.Provider value={{ user, profile, loading, login, register, logout, selectProfile, switchProfile, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
